/**
 * Render-order bookkeeping for the drag glow.
 *
 * An x-ray glow is depth-tested against a depth buffer that, inside the
 * silhouette of its *occlusion group* (the parts one option emits, e.g. the
 * left and right handle of a mirrored preset), holds only that group's own
 * geometry. Building that buffer takes two colour-less depth passes over every
 * part of the group before any of the group's glows are drawn, and the only
 * thing that orders draws across independent React components is
 * `renderOrder`. Each group therefore gets its own band of three consecutive
 * render orders, and every part of the group draws its reset, depth and glow
 * pass at the group's three values. Fractional orders are fine: three.js sorts
 * the transparent list by plain numeric comparison.
 *
 * Bands, lowest first:
 *   989       hover glow (depth-tested against the untouched scene)
 *   990.xxx   x-ray groups, one slot per group
 *   999       movement-area outline (`OUTLINE_RENDER_ORDER` in DraggablePart)
 *  1000       direction arrows (opaque, so drawn before all of the above anyway)
 */

/** The plain hover glow, drawn before any x-ray group touches the depth buffer. */
export const HOVER_GLOW_RENDER_ORDER = 989;
/** First x-ray group band. */
export const XRAY_GLOW_ORDER_BASE = 990;
/** Distance between two groups' bands. */
export const XRAY_GLOW_SLOT_STRIDE = 0.01;
/** Distance between the three passes inside one band. */
export const XRAY_GLOW_PASS_STEP = 0.001;
/** Every pass must stay below this: the outline is drawn there. */
export const XRAY_GLOW_ORDER_CEILING = 999;

/** The three render orders of one occlusion group. */
export interface XrayGlowPassOrders {
  /** Writes the part's own depth unconditionally, erasing the case's. */
  reset: number;
  /** Lowers that to the nearest surface across the whole group. */
  depth: number;
  /** The tint, depth-tested against the group-local buffer. */
  glow: number;
}

/** Render orders of the group holding `slot`. */
export function xrayGlowPassOrders(slot: number): XrayGlowPassOrders {
  const reset = XRAY_GLOW_ORDER_BASE + slot * XRAY_GLOW_SLOT_STRIDE;
  return {
    reset,
    depth: reset + XRAY_GLOW_PASS_STEP,
    glow: reset + 2 * XRAY_GLOW_PASS_STEP,
  };
}

interface SlotEntry {
  slot: number;
  holders: number;
}

/**
 * Hands out one slot per occlusion group for as long as at least one part of
 * the group holds it. Freed slots are reused smallest-first, so the bands in
 * use stay packed at the bottom however often parts mount and unmount.
 */
export class OcclusionSlotRegistry {
  private readonly held = new Map<string, SlotEntry>();
  /** Freed slots, kept descending so `pop()` yields the smallest. */
  private readonly free: number[] = [];
  private next = 0;

  /** Slot of `group`, allocating one on first use. */
  acquire(group: string): number {
    const entry = this.held.get(group);
    if (entry) {
      entry.holders += 1;
      return entry.slot;
    }
    const slot = this.free.pop() ?? this.next++;
    this.held.set(group, { slot, holders: 1 });
    return slot;
  }

  /** Drops one holder of `group`; the slot is freed with the last one. */
  release(group: string): void {
    const entry = this.held.get(group);
    if (!entry) return;
    entry.holders -= 1;
    if (entry.holders > 0) return;
    this.held.delete(group);
    this.free.push(entry.slot);
    this.free.sort((a, b) => b - a);
  }

  /** Slot currently held by `group`, if any. */
  slotOf(group: string): number | undefined {
    return this.held.get(group)?.slot;
  }

  /** Number of groups currently holding a slot. */
  get size(): number {
    return this.held.size;
  }
}

/** The registry every x-ray glow on the canvas shares. */
export const xrayGlowSlots = new OcclusionSlotRegistry();
